/**
 * Scrolling to a PDF citation and painting its highlight.
 *
 * These are the steps the PDF viewer runs once its settling delay has expired:
 * move the reader to the matched page the first time it is matched, then paint
 * the planned highlight boxes into each page's overlay, or, where no plan was
 * made, the legacy single-page highlight found by searching the page's text.
 *
 * Every rectangle here is in REFERENCE_SCALE units, the units the overlay is
 * sized in before `PDFViewer` transforms it onto the live canvas. Painting
 * needs no conversion for that reason. The scroll is the one consumer that has
 * to know where a box lands on screen, so it is handed the overlay's own
 * transform factor (`scale / REFERENCE_SCALE`) rather than deriving one.
 */

import { findTextMatch } from "@/utils/textMatching";
import { logger } from "@/telemetry";

import {
  WHITESPACE_RUN,
  itemToRect,
  type CitationTextItem,
  type HighlightRect,
  type PageHighlightPlan,
} from "./pdfCitationTypes";

/**
 * How much of the viewport is left above a citation when it is scrolled to, so the
 * passage carries context rather than sitting flush against the top edge, which reads
 * as the document having been cut off. A fifth keeps the passage high enough to be the
 * first thing seen. It is a fraction of the viewport rather than a fixed number of
 * pixels so that it stays proportionate, though in a short viewport, such as the
 * citation panel in a narrow layout, a fifth is relatively few pixels.
 */
const SCROLL_MARGIN_VIEWPORT_FRACTION = 0.2;

const HIGHLIGHT_BOX_CLASS =
  "absolute bg-warning/30 dark:bg-warning/40 border border-warning rounded";

/** Everything the viewer knows when its settling delay expires. */
export interface CitationView {
  /** The column the pages are rendered into, whose children are the pages in order. */
  container: HTMLElement;
  matchedPage: number;
  /**
   * Page the view has already been scrolled to. A zoom re-runs the viewer's effect
   * to redraw the overlay, and scrolling again would drag the reader back to the
   * citation every time they pressed zoom in.
   */
  lastScrolledPageRef: { current: number | null };
  /** Whether the plan came from stored coordinates rather than a text search. */
  measured: boolean;
  /** The scroll container around the pages. */
  scroller: HTMLElement | null;
  /** The overlay's transform onto the live canvas, being `scale / REFERENCE_SCALE`. */
  overlayScale: number;
  pageHighlights: PageHighlightPlan;
  highlightLayers: Map<number, HTMLDivElement>;
  citationText: string | undefined;
  textLayer: CitationTextItem[];
}

interface ItemRange {
  first: number;
  last: number;
}

const createHighlightBox = (rect: HighlightRect): HTMLDivElement => {
  const box = document.createElement("div");

  box.className = HIGHLIGHT_BOX_CLASS;
  box.style.left = `${rect.left}px`;
  box.style.top = `${rect.top}px`;
  box.style.width = `${rect.width}px`;
  box.style.height = `${rect.height}px`;
  box.style.pointerEvents = "none";

  return box;
};

/**
 * Scrolls the scroller so `box` sits a fifth of the viewport below its top edge.
 *
 * The box's on-screen offset within its page is its REFERENCE_SCALE offset carried
 * through the overlay's transform, which is why `overlayScale` has to be the overlay's
 * own factor. Disagreeing with it sends the reader somewhere the highlight is not.
 */
const scrollToBox = (
  pageElement: HTMLElement,
  box: HighlightRect,
  scroller: HTMLElement,
  overlayScale: number,
): void => {
  const pageTop =
    pageElement.getBoundingClientRect().top -
    scroller.getBoundingClientRect().top +
    scroller.scrollTop;
  const margin = scroller.clientHeight * SCROLL_MARGIN_VIEWPORT_FRACTION;
  const boxTop = box.top * overlayScale;

  scroller.scrollTo({
    top: Math.max(0, pageTop + boxTop - margin),
    behavior: "auto",
  });
};

/**
 * Moves the reader to the matched page.
 *
 * "auto" rather than "smooth" when the citation was measured. The page is known
 * from the start, so the destination is not a move the reader asked for from
 * somewhere they can see but the place the document should open at, and animating
 * from page 1 across a document of a few hundred pages hides the very thing being
 * opened. "auto" also defers to a reader's reduced-motion preference, which an
 * explicit "smooth" overrides.
 *
 * Kept smooth on the text-matching path, where the page is only known after every
 * page has been extracted, so the reader has already been looking at page 1 and an
 * instant jump would read as a glitch.
 *
 * A MEASURED CITATION SCROLLS TO THE BOX, NOT TO THE PAGE.
 *
 * `scrollIntoView({ block: "center" })` centres the PAGE, and a page at ordinary
 * zoom is a good deal taller than the viewport, so centring it puts its midpoint on
 * screen and nothing else. A passage in the lower third of the page therefore landed
 * below the fold and the reader had to scroll to find the very thing the citation
 * opened, which reads as the highlight having missed.
 *
 * It went unnoticed until the page became reliable: before anchoring, every citation
 * scrolled from page one and landed approximately, so approximate was the baseline.
 *
 * The text-matching path deliberately keeps centring the page. Its boxes are
 * available here too, since it sets `pageHighlights` and `matchedPage` in the same
 * batch the measured path does, so this is a choice rather than a constraint. The
 * smooth travel that path performs is worth keeping, and it is the path this change
 * is least able to verify. The consequence worth recording is that a text-matched
 * citation low on a tall page still lands below the fold, so LSEG-146 is closed for
 * documents the platform can measure and left open for those it cannot.
 */
const scrollToCitation = (pageElement: HTMLElement, view: CitationView): void => {
  const { matchedPage, measured, scroller } = view;
  const firstBox = (view.pageHighlights[matchedPage] ?? [])
    .slice()
    .sort((a, b) => a.top - b.top)[0];
  // Decided once, so the decision logged and the decision taken cannot drift apart.
  const boxScroll = measured && firstBox && scroller ? { box: firstBox, scroller } : null;

  logger.info("📜 Moving to page", {
    matchedPage,
    measured: String(measured),
    toBox: String(boxScroll !== null),
  });

  if (boxScroll) {
    scrollToBox(pageElement, boxScroll.box, boxScroll.scroller, view.overlayScale);
  } else {
    pageElement.scrollIntoView({
      behavior: measured ? "auto" : "smooth",
      block: "center",
    });
  }
};

/** Paints every planned page, including each page of a citation that spans several. */
const paintPlannedHighlights = (
  pages: number[],
  plan: PageHighlightPlan,
  layers: Map<number, HTMLDivElement>,
): void => {
  logger.info("🎨 Applying cross-page highlights", { pages: pages.join(", ") });
  for (const p of pages) {
    const layer = layers.get(p);

    if (!layer) continue;
    layer.innerHTML = "";
    const rects = plan[p] || [];

    for (const r of rects) {
      layer.appendChild(createHighlightBox(r));
    }
    logger.info("🎨 Added highlights on page", { highlightCount: rects.length, page: p });
  }
};

/**
 * The items from the first holding the match's first word to the last holding its
 * last word, or null when either word is on no item.
 */
const findFallbackRange = (items: CitationTextItem[], matchedText: string): ItemRange | null => {
  const words = matchedText.trim().split(WHITESPACE_RUN);
  const firstWord = words[0].toLowerCase();
  const lastWord = words[words.length - 1].toLowerCase();
  let firstIndex = -1;
  let lastIndex = -1;
  let foundFirst = false;

  items.forEach((item, index) => {
    const itemTextLower = item.text.toLowerCase();

    if (!foundFirst && itemTextLower.includes(firstWord)) {
      firstIndex = index;
      foundFirst = true;
    }
    if (foundFirst && itemTextLower.includes(lastWord)) {
      lastIndex = index;
    }
  });

  return firstIndex !== -1 && lastIndex !== -1 ? { first: firstIndex, last: lastIndex } : null;
};

/** Fallback: single-page highlight (legacy behaviour), found by searching the page's text. */
const paintFallbackHighlight = (
  layer: HTMLDivElement | undefined,
  textLayer: CitationTextItem[],
  matchedPage: number,
  citationText: string,
): void => {
  if (!layer) return;
  layer.innerHTML = "";
  const pageTextItems = textLayer.filter((item) => item.page === matchedPage);
  const pageText = pageTextItems.map((item) => item.text).join(" ");
  const matchResult = findTextMatch(pageText, citationText);

  if (!matchResult) return;
  const range = findFallbackRange(pageTextItems, matchResult.matchedText);

  if (!range) return;
  for (let i = range.first; i <= range.last; i++) {
    const item = pageTextItems[i];

    layer.appendChild(createHighlightBox(itemToRect(item)));
  }
  logger.info("🎨 Added highlights on page (fallback)", {
    highlightCount: range.last - range.first + 1,
    page: matchedPage,
  });
};

/** Paints the cross-page plan where there is one, otherwise the fallback. */
const paintCitationHighlights = (view: CitationView): void => {
  const pages = Object.keys(view.pageHighlights).map((n) => parseInt(n, 10));

  if (pages.length > 0) {
    paintPlannedHighlights(pages, view.pageHighlights, view.highlightLayers);
  } else if (view.citationText) {
    paintFallbackHighlight(
      view.highlightLayers.get(view.matchedPage),
      view.textLayer,
      view.matchedPage,
      view.citationText,
    );
  }
};

/**
 * Scrolls to the matched page the first time it is matched, then paints its
 * highlight. Logs and does nothing when the page has not been rendered yet; the
 * viewer runs this again once the render pass ends.
 */
export const applyCitationView = (view: CitationView): void => {
  const pageElement = view.container.children[view.matchedPage - 1] as HTMLElement;

  if (!pageElement) {
    logger.info("⚠️ Page element not found (may still be rendering)", {
      matchedPage: view.matchedPage,
    });

    return;
  }

  // Only scroll the first time a given page is matched, so a zoom repaints the
  // overlay without dragging the reader back to the citation.
  if (view.lastScrolledPageRef.current !== view.matchedPage) {
    scrollToCitation(pageElement, view);
    view.lastScrolledPageRef.current = view.matchedPage;
  }

  paintCitationHighlights(view);
};
